
/*
// USAGE (HTML):

<button
  id="accordion-trigger-id"
  data-accordion="true | group-name"
  aria-expanded="true | false"
  aria-controls="accordion-content-id">
  BUTTON LABEL
</button>

<div
  role="region"
  id="accordion-content-id"
  aria-hidden="false | true"
  aria-labelledby="accordion-trigger-id">
  <div> <!-- ⚠️ This inner div is required -->
    ACCORDION CONTENT
  </div>
</div>

-------------------------------------------------
⚠️ In order to have toggleable accordions a.k.a
   cliking on one closes others, you need to set
   "data-accordion" attribute to one group-name.
-------------------------------------------------

// USAGE (JS):

import Accordion from "accordion";

pie.plugin(async props => Accordion());

*/


export default function Accordion() {

  const accordionsCache = new WeakMap();
  const accordionsGroupCache = new Map();

  document.addEventListener("click", event => {
    if (event.target.matches("*[data-accordion]")) {

      if (!verifyAccordionTriggerProps(event.target)) {
        throw new Error("Accordion element must have id, aria-expanded and aria-controls attributes");
      }

      if (isGroupAccordion(event.target)) {
        const groupName = event.target.dataset.accordion;
        const group = Array.from(document.querySelectorAll(`*[data-accordion="${groupName}"]`))
          .map(trigger => {
            cacheAccordion(trigger, accordionsCache);
            return trigger;
          });
        accordionsGroupCache.set(groupName, group);
      }

      let trigger = event.target;
      let accordion = accordionsCache.get(trigger) || cacheAccordion(trigger, accordionsCache);

      // Close all accordions in group.
      if (accordionsGroupCache.has(trigger.dataset.accordion)) {
        accordionsGroupCache.get(trigger.dataset.accordion).forEach(trigger => {
          setAccordionState(trigger, accordionsCache.get(trigger), false);
        });
        setAccordionState(trigger, accordion, accordion.open);
      }
      else {
        setAccordionState(trigger, accordion, !accordion.open);
      }

    }
  });
}

// ---- Helpers ----------------

function verifyAccordionTriggerProps(element) {
  return element && element.hasAttribute("id") && element.hasAttribute("aria-expanded") && element.hasAttribute("aria-controls");
}

function verifyAccordionTargetProps(element) {
  return element && element.hasAttribute("id") && element.hasAttribute("role") && element.hasAttribute("aria-hidden") && element.hasAttribute("aria-labelledby");
}

function isGroupAccordion(element) {
  return typeof element.dataset.accordion === "string" && element.dataset.accordion !== "true";
}

function cacheAccordion(trigger, accordionsCache) {
  const target = document.getElementById(trigger.getAttribute("aria-controls"));

  if (!verifyAccordionTargetProps(target)) {
    throw new Error("Accordion target element must have id, role, aria-hidden and aria-labelledby attributes");
  }

  let accordion = {
    target,
    open: trigger.getAttribute("aria-expanded") === "true",
  };

  accordionsCache.set(trigger, accordion);
  return accordion;
}

function setAccordionState(trigger, accordion, state) {
  trigger.setAttribute("aria-expanded", String(state));
  accordion.target.setAttribute("aria-hidden", String(!state));
  accordion.open = !accordion.open;
}